import {
  Action,
  CountBy,
  EntrySectionUpdateEvent,
  FiltersOperator,
  MetricEntryBehavior,
} from 'common/query-builder/query-entry-section/types';
import {MetricEntry, QueryEventEntry} from 'common/query-builder/query-entry-section/types';
import {BaseEntryRow} from 'common/query-builder/query-entry-section/row-types/base';
import {RetentionQuerySettingsUpdate} from 'common/report/retention/query-builder/retention-query-settings';
import {
  RetentionAlignmentUpdate,
  RetentionBucketSizeUpdate,
  RetentionUnitUpdate,
} from 'common/report/retention/query-builder/retention-criteria';
import {MetricType} from 'common/query-builder/query-block/constants';

// eslint-disable-next-line import/no-cycle
import {RetentionMetricEntryRowEl} from './template';

export class RetentionMetricEntryRow extends BaseEntryRow<MetricEntry> {
  template = RetentionMetricEntryRowEl;

  // retention metric entry row should always not be placeholder. so whatever get commited inside nested query-entry-sections, it should populate up
  isPlaceholder() {
    return false;
  }

  getCountingMethod(): string {
    const {retentionType, retentionUnbounded} = this.entry.behavior;
    const {cumulative} = this.entry.measurement;

    if (retentionType === `addiction`) {
      if (cumulative) {
        return `cumulative`;
      }
      return `non-cumulative`;
    }

    if (retentionUnbounded) {
      return `unbounded`;
    }
    return `bounded`;
  }

  handleEntryUpdate = (ev: EntrySectionUpdateEvent<QueryEventEntry>) => {
    const {entries} = ev.detail;
    const behaviors: MetricEntryBehavior[] = entries.map((entry) => ({
      id: entry.event.custom ? entry.event.id : null,
      type: entry.event.custom ? MetricType.CustomEvent : MetricType.Event,
      name: entry.event.value,
      filters: entry.filters,
      filtersDeterminer: entry.filtersOperator === FiltersOperator.Or ? `any` : `all`,
    }));
    const entry: MetricEntry = {
      ...this.entry,
      behavior: {
        ...this.entry.behavior,
        behaviors,
      },
    };
    this.section.updateEntries(Action.Edit, {entry, index: this.index});
  };

  handleSettingsChanged = (update: RetentionQuerySettingsUpdate) => {
    const entry: MetricEntry = {
      ...this.entry,
      behavior: {
        ...this.entry.behavior,
        retentionUnbounded: Boolean(update.detail.unbounded),
      },
      measurement: {
        ...this.entry.measurement,
        cumulative: Boolean(update.detail.cumulative),
      },
    };
    this.section.updateEntries(Action.Edit, {entry, index: this.index});

    // TODO: emit track event
  };

  handleRetentionUnitChanged = (update: RetentionUnitUpdate) => {
    const {retentionUnit} = update.detail;

    const entry: MetricEntry = {
      ...this.entry,
      behavior: {
        ...this.entry.behavior,
        retentionUnit,
      },
    };
    this.section.updateEntries(Action.Edit, {entry, index: this.index});
  };

  handleBucketSizesApplied = (update: RetentionBucketSizeUpdate) => {
    const {retentionUnit, bucketSizes} = update.detail;

    const entry: MetricEntry = {
      ...this.entry,
      behavior: {
        ...this.entry.behavior,
        retentionUnit,
        retentionCustomBucketSizes: bucketSizes,
      },
    };
    this.section.updateEntries(Action.Edit, {entry, index: this.index});
  };

  handleToggleRetentionAlignment = (update: RetentionAlignmentUpdate) => {
    const {retentionAlignmentType} = update.detail;

    const entry: MetricEntry = {
      ...this.entry,
      behavior: {
        ...this.entry.behavior,
        retentionAlignmentType,
      },
    };
    this.section.updateEntries(Action.Edit, {entry, index: this.index});
  };

  handleRetentionCountBySelected = (ev: CustomEvent<{item: CountBy}>) => {
    const countBy = ev.detail.item;
    const measurement = this.entry.measurement;

    measurement.aggregationOperator = countBy.aggregationOperator;
    if (countBy?.property) {
      measurement.property = countBy.property;
    }
    if (countBy?.advancedMenuValue) {
      measurement.retentionBucketIndex = countBy.advancedMenuValue as number;
    }

    const entry = {
      ...this.entry,
      measurement,
    };
    this.section.updateEntries(Action.Edit, {entry, index: this.index});
  };
}
